// src/components/Control/ControlPanel/LidarViewer.tsx
// Вид сверху на скан лидара из robot/<id>/scan (публикует docker/esp32-bridge):
//   {"timestamp": ms, "points": [[угол°, дистанция мм], ...]}
// Робот в центре, 0° лидара — вверх, углы растут по часовой стрелке.
import React, { useEffect, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';

interface LidarViewerProps {
  zenohSession: zenoh.Session | null;
  scanKey: string;
}

interface Scan {
  timestamp: number;
  points: [number, number][];
}

// Через сколько без новых сканов считать поток остановившимся
const STALE_MS = 3000;
// Шаги колец дальности, мм: выбирается первый, при котором колец не больше 5
const RING_STEPS_MM = [250, 500, 1000, 2000, 5000];

function niceRange(points: [number, number][]): number {
  // 95-й перцентиль дальности, чтобы одиночные дальние выбросы не сжимали картинку
  const d = points.map(p => p[1]).sort((a, b) => a - b);
  const p95 = d.length ? d[Math.floor(d.length * 0.95)] : 1000;
  return Math.max(500, Math.ceil((p95 * 1.1) / 500) * 500);
}

function draw(canvas: HTMLCanvasElement, scan: Scan | null) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  // Холст — по размеру блока, с учётом плотности пикселей
  const dpr = window.devicePixelRatio || 1;
  const w = Math.max(50, canvas.clientWidth);
  const h = Math.max(50, canvas.clientHeight);
  if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) / 2 - 12;

  ctx.fillStyle = '#1e222c';
  ctx.fillRect(0, 0, w, h);

  const points = scan?.points ?? [];
  const rangeMm = niceRange(points);
  const scale = radius / rangeMm;

  // Кольца дальности
  const step = RING_STEPS_MM.find(s => rangeMm / s <= 5) ?? 5000;
  ctx.strokeStyle = '#3a4050';
  ctx.fillStyle = '#7d8596';
  ctx.font = '10px sans-serif';
  ctx.lineWidth = 1;
  for (let r = step; r <= rangeMm; r += step) {
    ctx.beginPath();
    ctx.arc(cx, cy, r * scale, 0, 2 * Math.PI);
    ctx.stroke();
    ctx.fillText(r >= 1000 ? `${r / 1000} м` : `${r} мм`, cx + 3, cy - r * scale - 2);
  }
  // Оси
  ctx.beginPath();
  ctx.moveTo(cx, cy - radius);
  ctx.lineTo(cx, cy + radius);
  ctx.moveTo(cx - radius, cy);
  ctx.lineTo(cx + radius, cy);
  ctx.stroke();

  // Точки скана
  ctx.fillStyle = '#5aa0ff';
  for (const [deg, mm] of points) {
    const a = (deg * Math.PI) / 180;
    const x = cx + Math.sin(a) * mm * scale;
    const y = cy - Math.cos(a) * mm * scale;
    ctx.fillRect(x - 1, y - 1, 2, 2);
  }

  // Робот и направление 0°
  ctx.fillStyle = '#ffdc50';
  ctx.beginPath();
  ctx.moveTo(cx, cy - 9);
  ctx.lineTo(cx - 6, cy + 6);
  ctx.lineTo(cx + 6, cy + 6);
  ctx.closePath();
  ctx.fill();
}

const LidarViewer: React.FC<LidarViewerProps> = ({ zenohSession, scanKey }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scanRef = useRef<Scan | null>(null);
  const frameRef = useRef<number | null>(null);
  const [info, setInfo] = useState<{ points: number; hz: number; lastMs: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Размер блока изменился — перерисовать
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => draw(canvas, scanRef.current));
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (canvasRef.current) draw(canvasRef.current, null);
    setInfo(null);
    setError(null);
    scanRef.current = null;
    if (!zenohSession) return;

    let cancelled = false;
    let subscriber: zenoh.Subscriber | null = null;
    const arrivals: number[] = [];

    const onSample = (sample: zenoh.Sample) => {
      let scan: Scan;
      try {
        scan = JSON.parse(sample.payload().toString());
      } catch {
        return;
      }
      if (!Array.isArray(scan.points)) return;
      scanRef.current = scan;
      const now = Date.now();
      arrivals.push(now);
      while (arrivals.length && now - arrivals[0] > 2000) arrivals.shift();
      // Рисуем не чаще кадра браузера, даже если сканы приходят пачкой
      if (frameRef.current === null) {
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          if (canvasRef.current) draw(canvasRef.current, scanRef.current);
          setInfo({ points: scan.points.length, hz: arrivals.length / 2, lastMs: now });
        });
      }
    };

    (async () => {
      try {
        const sub = await zenohSession.declareSubscriber(scanKey, { handler: onSample });
        if (cancelled) {
          await sub.undeclare();
        } else {
          subscriber = sub;
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      if (subscriber) {
        subscriber.undeclare().catch(err => console.warn('LidarViewer: undeclare failed', err));
      }
    };
  }, [zenohSession, scanKey]);

  // Перерисовка статуса «нет данных», когда сканы перестали приходить
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const stale = !info || Date.now() - info.lastMs > STALE_MS;

  let overlay: string | null = null;
  if (!zenohSession) overlay = 'Подключитесь к Zenoh';
  else if (error) overlay = `Ошибка подписки: ${error}`;
  else if (stale) overlay = 'Нет данных лидара';

  return (
    <div className="lidar-viewer op-lidar">
      <canvas ref={canvasRef} aria-label="Вид лидара сверху" role="img" />
      {overlay && <div className="op-map-msg">{overlay}</div>}
      <div className="op-lidar-caption">
        Лидар {info && !stale ? `· ${info.points} точек · ${info.hz.toFixed(1)} скан/с` : ''} · 0° вверх
      </div>
    </div>
  );
};

export default LidarViewer;
